import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch,
  Space, Popconfirm, message, Select, Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getBranches } from '../../api/branches';
import BranchSelect from '../common/BranchSelect';
import { getCampaigns, createCampaign, updateCampaign, deleteCampaign } from '../../api/settings';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import useServerSort from '../../hooks/useServerSort';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;

const CampaignsTab = ({ branchId } = {}) => {
  const [campaigns, setCampaigns] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form] = Form.useForm();
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('campaigns', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const [branchFilter, setBranchFilter] = useState(branchId ?? null);
  const { sortField, sortOrder, applySorter } = useServerSort();

  const fetchCampaigns = async (page = 1, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const res = await getCampaigns({ page, per_page: perPage, branch_id: branchFilter || undefined, sort_by: sortBy || undefined, sort_direction: sortDir || undefined });
      setCampaigns(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch (error) {
      message.error('Kampanyalar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await getBranches({ per_page: 100 });
      setBranches(res.data.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchCampaigns(1);
  }, [branchFilter]);

  const handleSubmit = async (values) => {
    try {
      const data = { ...values, ...(branchId ? { branch_id: branchId } : {}) };
      if (editingCampaign) {
        await updateCampaign(editingCampaign.id, data);
        message.success('Kampanya güncellendi.');
      } else {
        await createCampaign(data);
        message.success('Kampanya oluşturuldu.');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    form.setFieldsValue(campaign);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCampaign(id);
      message.success('Kampanya silindi.');
      fetchCampaigns();
    } catch (error) {
      message.error('Kampanya silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Kampanya Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : null,
    },
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      sortOrder: sortField === 'type' ? sortOrder : null,
      render: (type) => (
        <Tag color={type === 'percentage' ? 'blue' : 'green'}>
          {type === 'percentage' ? 'Yüzde' : 'Sabit'}
        </Tag>
      ),
    },
    {
      title: 'Değer',
      key: 'value',
      sorter: true,
      sortOrder: sortField === 'value' ? sortOrder : null,
      render: (_, r) => r.type === 'percentage' ? `%${r.value}` : `${r.value} ₺`,
    },
    {
      title: 'Şube',
      dataIndex: 'branch_id',
      key: 'branch_id',
      render: (branch_id) => {
        const branch = branches.find(b => b.id === branch_id);
        return branch ? branch.name : '—';
      },
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      sortOrder: sortField === 'is_active' ? sortOrder : null,
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateCampaign(record.id, { is_active: checked });
              message.success('Durum güncellendi.');
              fetchCampaigns();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Kampanyayı silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end items-center mb-4 gap-3">
        {!branchId && (
          <Select
            placeholder="Şube filtrele"
            allowClear
            style={{ width: 180 }}
            value={branchFilter}
            onChange={v => setBranchFilter(v ?? null)}
          >
            {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
          </Select>
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCampaign(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Kampanya
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={campaigns}
        rowKey="id"
        loading={loading}
        onChange={(paginationInfo, _filters, sorter) => {
          const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
          setPersistedPageSize(paginationInfo.pageSize);
          fetchCampaigns(paginationInfo.current, paginationInfo.pageSize, sField, sDir);
        }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['15', '30', '50', '100'],
          showTotal: (total) => `Toplam ${total} kampanya`,
        }}
      />

      <Modal
        title={editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Kampanya Adı" rules={[{ required: true }]}>
            <Input placeholder="Kardeş İndirimi" maxLength={100} />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="type" label="İndirim Türü" rules={[{ required: true }]}>
            <Select placeholder="Tür seçiniz">
              <Option value="percentage">Yüzde (%)</Option>
              <Option value="fixed">Sabit (₺)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="value" label="Değer" rules={[{ required: true }]}>
            <Input type="number" min="0" placeholder="10" />
          </Form.Item>
          {!branchId && (
            <Form.Item name="branch_id" label="Şube" rules={[{ required: true }]}>
              <BranchSelect branches={branches} />
            </Form.Item>
          )}
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingCampaign ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CampaignsTab;